// lib/feed/prefs-strip-cache.ts
//
// Pure domain for the client-side cache of the RESOLVED feed preference
// filters — the `FeedFiltersState` partial `live-feed.tsx`'s
// `loadPrefsIntoStrip` computes via `collectorKeysToFeedFiltersPartial`.
// Keyed by user id, sessionStorage, short TTL.
//
// WHY (documented in .tondocs/infra/PERF_NAV_2026_07_16.md): live-feed.tsx
// gates the listings fetch on async prefs resolution (`prefsPhase`). On every
// fresh mount `strip` starts as EMPTY_STRIP, so `usePaginatedFeed`'s snapshot
// key (hooks/use-paginated-feed.ts) is computed off the EMPTY filters on
// render one; once prefs resolve, `filtersKey` changes and the hook hydrates
// the NEW key's snapshot for instant paint (its branch (2)) — but that path
// deliberately skips scroll restore (a filter switch is expected to land at
// the top). The hook's actual mount-time scroll-restore effect
// (`initialSnapshotRef`) only ever looks at the snapshot key computed on the
// VERY FIRST render, which for a logged-in user with resolved prefs is
// already stale by the time prefs resolve. Net effect: logged-in users with
// preferences get instant paint but lose their scroll position on back-nav.
//
// Fix: cache the resolved partial so the LAZY INITIALIZER can seed the real
// `strip` (and `prefsPhase`) synchronously on mount — the snapshot key is then
// correct from render one, and the existing scroll-restore path just works.
//
// hex: pure serialize/parse + key derivation, no DOM/window access — the
// component (live-feed.tsx) does the sessionStorage I/O in a lazy initializer
// + a write after `loadPrefsIntoStrip` resolves. Deliberately simpler than
// `lib/taxonomy-cache.ts`'s injectable-factory + single-flight design: this
// cache has exactly one reader (the mount initializer) and one writer (the
// background prefs fetch) per component instance — there's no concurrent-
// fetch fan-out to coalesce, so the extra factory ceremony isn't earning its
// keep here (DRY §6 / hex §6 "skip when the cost isn't earning the abstraction").
//
// @see components/feed/live-feed.tsx (adapter)
// @see lib/taxonomy-cache.ts (sibling client cache; heavier design, different need)
// @see lib/feed/feed-snapshot.ts (sibling — same parse/serialize/TTL shape)
// @see .tondocs/infra/PERF_NAV_2026_07_16.md
// @see .tondocs/skills/hex.md §1

import type { FeedFiltersState } from "@/components/feed/feed-filters";

/** Namespace all prefs-strip cache keys. Bump the version suffix if the persisted shape changes. */
export const PREFS_STRIP_CACHE_PREFIX = "kraahl:feed-prefs-strip:v1:";

/** ~10 min TTL — long enough for the common listing->back round-trip; short
 *  enough that a stale onboarding/prefs edit from long ago doesn't stick. */
export const PREFS_STRIP_CACHE_TTL_MS = 10 * 60_000;

export interface PrefsStripCacheEntry {
  strip: Partial<FeedFiltersState>;
  ts: number;
}

/** Per-user cache key — prefs are user-scoped, so a different signed-in user
 *  (or logged-out, which never calls this) never sees another user's cache. */
export function buildPrefsStripCacheKey(userId: string): string {
  return `${PREFS_STRIP_CACHE_PREFIX}${userId}`;
}

/** Serialize the resolved partial + write timestamp for storage. */
export function serializePrefsStripCache(strip: Partial<FeedFiltersState>, ts: number): string {
  const entry: PrefsStripCacheEntry = { strip, ts };
  return JSON.stringify(entry);
}

/**
 * Parse + validate a raw sessionStorage value into the resolved strip
 * partial, or null. Rejects malformed / non-object / stale entries. Pure —
 * `now`/`ttlMs` are injected so tests are deterministic.
 */
export function parsePrefsStripCache(
  raw: string | null,
  now: number,
  ttlMs: number
): Partial<FeedFiltersState> | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const p = parsed as Partial<PrefsStripCacheEntry>;
  if (typeof p.strip !== "object" || p.strip === null) return null;
  const ts = typeof p.ts === "number" ? p.ts : 0;
  if (now - ts > ttlMs) return null;
  return p.strip as Partial<FeedFiltersState>;
}
